import axios from 'axios';
import { useCallback } from 'react';
import { trackPromise } from 'react-promise-tracker';
import { useHistory, useLocation } from 'react-router-dom';

import { loginUser } from 'store/reducers/user.slice';

import { errorMessage } from 'constants/pop-up-messages';
import { PROMISES_AREA } from 'constants/promises-area';

import { useAppDispatch } from 'services/hooks/redux';
import { storageService } from 'services/storage/storage';
import { googleAuth } from 'services/user.service';
import {
  buildGoogleAuthUrl,
  decodeGoogleOAuthState,
  encodeGoogleOAuthState,
  getGoogleRedirectUri,
  IGoogleOAuthState,
  resolveGoogleAuthFallback,
  resolveGoogleAuthRedirect,
} from 'services/utils/google-auth';

const GOOGLE_AUTH_ERROR =
  'Google sign-in failed. Try again, or sign in another way';

export const useGoogleAuth = () => {
  const dispatch = useAppDispatch();
  const { replace } = useHistory();
  const location = useLocation<ILocationState>();

  const startGoogleAuth = useCallback(() => {
    const clientId = process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID;

    if (!clientId) {
      errorMessage('Google sign-in is not configured').fire();
      return;
    }

    const state = encodeGoogleOAuthState({
      returnTo: `${location.pathname}${location.search}`,
      from: location.state?.from,
    });

    window.location.assign(
      buildGoogleAuthUrl({
        clientId,
        redirectUri: getGoogleRedirectUri(),
        state,
      })
    );
  }, [location.pathname, location.search, location.state]);

  const completeGoogleAuth = useCallback(
    async (code: string, oauthState: IGoogleOAuthState) => {
      try {
        const { data } = await trackPromise(
          googleAuth({
            code,
            redirectUri: getGoogleRedirectUri(),
          }),
          PROMISES_AREA.auth
        );

        dispatch(loginUser(data.user));
        storageService.setToken(data.token, true);
        replace(resolveGoogleAuthRedirect(oauthState));
      } catch (error) {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.message || GOOGLE_AUTH_ERROR
          : GOOGLE_AUTH_ERROR;

        await errorMessage(message).fire();
        replace(resolveGoogleAuthFallback(oauthState));
      }
    },
    [dispatch, replace]
  );

  const handleGoogleAuthReturn = useCallback(
    (search: string) => {
      const params = new URLSearchParams(search);
      const oauthState = decodeGoogleOAuthState(params.get('state'));
      const error = params.get('error');
      const code = params.get('code');

      if (error || !code) {
        errorMessage(GOOGLE_AUTH_ERROR)
          .fire()
          .finally(() => replace(resolveGoogleAuthFallback(oauthState)));
        return;
      }

      return completeGoogleAuth(code, oauthState);
    },
    [completeGoogleAuth, replace]
  );

  return {
    startGoogleAuth,
    handleGoogleAuthReturn,
  };
};
